import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingTable } from "@/components/ui/system-state";
import { Button } from "@/components/ui/button";
import { WipHint } from "@/components/ui/wip-hint";
import { toast } from "@/components/ui/toast";
import { useInvoicingSettings } from "@/hooks/use-invoices";
import { updateInvoicingSettings } from "@/features/invoicing/api";
import { idempotencyKey } from "@/lib/api";
import { useInvoicingScope } from "@/features/invoicing/invoices-list/use-invoicing-scope";

import { SETTINGS_COPY as COPY, isDeniedCode } from "@/features/invoicing/settings/copy";
import {
  buildUpdate,
  draftEquals,
  draftFromSettings,
  type SettingsDraft,
} from "@/features/invoicing/settings/settings-model";
import {
  BillingProfileCard,
  CollectionWalletsCard,
  DraftErrors,
  NetworkLimitsCard,
  SettingsRefusal,
  TermsCard,
} from "@/features/invoicing/settings/settings-sections";

/**
 * `/stablecoins/invoices/settings` — collection wallets, default terms, expiry
 * grace, dust and minimum amounts, and the billing profile whose legal name
 * comes read-only from KYB verification (page spec `invoicing-settings`,
 * ALL-5908).
 *
 * Data: `GET /v1/invoicing/settings` through `useInvoicingSettings`; the only
 * write is `PUT /v1/invoicing/settings` with the row's `version` as the
 * optimistic-concurrency token and a fresh `Idempotency-Key` per save.
 *
 * Three things this page deliberately does not do:
 *
 *   - **No default configuration.** A refusal renders the refusal; it never
 *     falls back to a set of wallets, limits or terms the org does not have.
 *   - **No client-side clamping.** The service owns the expiry-grace window and
 *     every floor. A value outside it is refused by the server and the refusal
 *     is shown, because a silently adjusted number would be saved as a setting
 *     the operator never chose.
 *   - **No approval controls.** This page can read and save settings and nothing
 *     else; `invoice:create`, `invoice:issue`, `invoice:void` and both approval
 *     permissions are out of reach from here by construction.
 */
export default function InvoicingSettingsPage() {
  const canManage = useInvoicingScope("invoice:settings.manage");
  const { data: settings, loading, error, refresh } = useInvoicingSettings();

  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [refusal, setRefusal] = useState<{
    reasonCode: string;
    supportReference?: string;
    capability?: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  // The draft is seeded from (and re-seeded by) whatever the service last sent,
  // so a successful save or a manual refresh replaces the form with live values
  // rather than leaving an edit built on a stale row.
  useEffect(() => {
    setDraft(settings ? draftFromSettings(settings) : null);
    setFieldErrors([]);
    setRefusal(null);
  }, [settings, canManage]);

  const save = useCallback(async () => {
    if (!canManage || !settings || !draft || saving) return;
    const built = buildUpdate(settings, draft);
    if (!built.ok) {
      setFieldErrors(built.errors);
      return;
    }
    setFieldErrors([]);
    setRefusal(null);
    setSaving(true);
    // One key per save attempt: a key is reused across retries of the *same*
    // action, and pressing Save again is a new action.
    const key = idempotencyKey("invoicing_settings_update");
    const result = await updateInvoicingSettings(built.body, { idempotencyKey: key });
    setSaving(false);
    if (result.ok) {
      toast({ variant: "success", title: COPY.savedToast });
      refresh();
      return;
    }
    setRefusal({
      reasonCode: result.error.reasonCode,
      supportReference: result.error.supportReference,
      capability: result.error.capability,
    });
  }, [canManage, settings, draft, saving, refresh]);

  const header = (
    <PageHeader title={COPY.title} subtitle={COPY.subtitle} actions={<WipHint />} />
  );

  if (loading && !settings) {
    return (
      <div>
        {header}
        <LoadingTable rows={4} cols={3} label={COPY.loadingLabel} />
      </div>
    );
  }

  // A failed read is the page's error state: the reason code the service sent and
  // its support reference, never a configuration rendered from nothing. A 403 is
  // the access-denied state, because the next step there is a permission.
  if (error && !settings) {
    return (
      <div>
        {header}
        <SettingsRefusal
          reasonCode={error.reasonCode}
          supportReference={error.supportReference}
          capability={error.capability}
          onRetry={refresh}
        />
        {isDeniedCode(error.reasonCode) && <p className="mt-3 text-xs text-muted">{COPY.readonlyHint}</p>}
      </div>
    );
  }

  if (!settings || !draft) {
    // The hook settled with neither data nor error: nothing to show and nothing
    // to report, so the page says exactly that rather than inventing a state.
    return (
      <div>
        {header}
        <p className="text-sm text-muted">{COPY.noDataBody}</p>
      </div>
    );
  }

  const dirty = !draftEquals(draft, draftFromSettings(settings));

  return (
    <div>
      {header}

      {refusal && (
        <SettingsRefusal
          className="mb-4"
          reasonCode={refusal.reasonCode}
          supportReference={refusal.supportReference}
          capability={refusal.capability}
          onRetry={refresh}
          retryLabel="Reload settings"
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <BillingProfileCard settings={settings} />
        <TermsCard settings={settings} draft={draft} onChange={setDraft} editable={canManage} />
        <CollectionWalletsCard draft={draft} onChange={setDraft} editable={canManage} />
        <NetworkLimitsCard draft={draft} errors={fieldErrors} onChange={setDraft} editable={canManage} />
      </div>

      {!canManage && <p className="mt-4 text-sm text-muted">{COPY.readonlyHint}</p>}
      {canManage && (
      <div className="mt-4 flex flex-col gap-3">
        <DraftErrors errors={fieldErrors} />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => void save()} loading={saving} disabled={!dirty || saving}>
            {saving ? COPY.savingLabel : COPY.saveLabel}
          </Button>
          <Button
            variant="secondary"
            disabled={!dirty || saving}
            onClick={() => setDraft(draftFromSettings(settings))}
          >
            {COPY.cancelLabel}
          </Button>
          <span className="text-xs text-muted">{dirty ? COPY.unsavedHint : COPY.noChangesHint}</span>
        </div>
      </div>
      )}
    </div>
  );
}
